import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import i18next from "src/i18n/config";
import { RootState } from "src/store";
import { makeStyles } from "tss-react/mui";

import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { useCurrentSubscription } from "@dashboard/workspaces/api/helpers";
import { Box, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import ApmLink from "@shared/ApmLink";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { HelpLinkBar } from "@shared/HelpLinkBar/HelpLinkBar";

import { selectDevicesByGroupIds } from "../devices/store/selectors/devices";

import DownloadsTable from "./components/DownloadsTable";
import { LegacyInstallersDialog } from "./components/LegacyInstallersDialog";
import { useReleaseFiles } from "./hooks/useReleaseFiles";

const useStyles = makeStyles()((theme) => ({
    contentLayout: {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
    },
    tutorialAlert: { marginBottom: theme.spacing(4), flexShrink: 0 },
    tutorialList: { listStyleType: "disc", marginLeft: theme.spacing(6), marginTop: theme.spacing(2) },
}));

const DownloadsPage = () => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages"]);
    const { data: currentSubscription } = useCurrentSubscription({});
    const groupIds = currentSubscription?.groupIds || [];
    const devices = useSelector((state: RootState) => selectDevicesByGroupIds(state, groupIds));
    const releaseFiles = useReleaseFiles();

    const [legacyDialogOpen, setLegacyDialogOpen] = React.useState(false);

    if (releaseFiles.status === "pending") {
        return <PageContainer isLoading />;
    }

    return (
        <PageContainer fitViewport>
            <ExpiredWrapper>
                <Box className={classes.contentLayout}>
                    <HelpLinkBar
                        label={t("pages:downloadsPage.legacyInstallers.triggerLink")}
                        tooltip={t("pages:downloadsPage.legacyInstallers.tooltip")}
                        onClick={() => setLegacyDialogOpen(true)}
                    />

                    {!devices.length ? (
                        <CustomAlert severity="info" className={classes.tutorialAlert}>
                            <Typography>{t("pages:downloadsPage.tutorial.title")}</Typography>
                            <ul className={classes.tutorialList}>
                                <li>
                                    <Typography>
                                        <ApmLink
                                            parent="Downloads page"
                                            title={t("pages:downloadsPage.tutorial.items.configuration")}
                                            href="https://emteria.com/kb/initial-configuration-setup-wizard"
                                        >
                                            {t("pages:downloadsPage.tutorial.items.configuration")}
                                        </ApmLink>
                                    </Typography>
                                </li>
                                <li>
                                    <Typography>
                                        <ApmLink
                                            parent="Downloads page"
                                            title={t("pages:downloadsPage.tutorial.items.management")}
                                            href="https://emteria.com/kb/enable-mdm"
                                        >
                                            {t("pages:downloadsPage.tutorial.items.management")}
                                        </ApmLink>
                                    </Typography>
                                </li>
                            </ul>
                        </CustomAlert>
                    ) : null}

                    <DownloadsTable data={releaseFiles.data} />
                </Box>
            </ExpiredWrapper>
            <LegacyInstallersDialog open={legacyDialogOpen} onClose={() => setLegacyDialogOpen(false)} />
        </PageContainer>
    );
};
export default withMetaDescription(DownloadsPage, {
    title: i18next.t("pages:downloadsPage.metaTitle"),
    description: i18next.t("pages:downloadsPage.metaDescription"),
});
